import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { Trash2, Edit2 } from 'react-feather';

import {
  canUserAccessBuyerBranch,
  collectBranchesFromBuyers,
  formatMoney,
  PromiseToPayWithUser,
} from '@dill/dill-shared';
import { twMerge } from 'tailwind-merge';
import { useAppSelector } from '../../../../../redux/store';
import { DillButton, DillTable, DillDropDownFilter, DillCheckbox } from '../../../../general/Dill';
import type { DillColumn } from '../../../../general/Dill';

interface PromiseToPaysTableProps {
  promiseToPays: PromiseToPayWithUser[];
  sortOptions: { sortOrder: 'asc' | 'desc'; sortBy: string };
  onSortChange: (sortOptions: { sortOrder: 'asc' | 'desc'; sortBy: string }) => void;
  onCancel?: (promiseToPayId: string) => void;
  onDelete: (promiseToPayId: string) => void;
  onEdit: (promise: PromiseToPayWithUser) => void;
  onRowClick: (promise: PromiseToPayWithUser) => void;
  toolbarRight?: React.ReactNode;
  searchValue?: string;
  onSearch?: (value: string) => void;
  showBuyerColumn?: boolean;
}

const sourceLabels: Record<string, string> = {
  PHONE: 'Phone',
  EMAIL: 'Email',
  TEXT: 'Text',
  IN_PERSON: 'In-Person',
};

const typeLabels: Record<string, string> = {
  FULL_PAYMENT: 'Full Payment',
  PARTIAL_PAYMENT: 'Partial Payment',
  PAYMENT_PLAN: 'Payment Plan',
};

const statusColors: Record<string, string> = {
  ACTIVE: 'bg-blue-100 text-blue-800',
  FULFILLED: 'bg-green-100 text-green-800',
  BROKEN: 'bg-red-100 text-red-800',
  CANCELLED: 'bg-gray-100 text-gray-800',
};

type PromiseToPayTableRow = PromiseToPayWithUser & {
  buyerNameFilter: string;
  createdByName: string;
};

const createCheckboxItem = (label: string, value: string, isChecked: boolean) => ({
  label,
  value,
  labelInElement: (
    <div className="flex mx-1 my-1 items-center gap-2">
      <DillCheckbox isChecked={isChecked} />
      <div className="text-black text-xs">{label}</div>
    </div>
  ),
});

function PromiseToPaysTable({
  promiseToPays,
  sortOptions,
  onSortChange,
  onCancel,
  onDelete,
  onEdit,
  onRowClick,
  toolbarRight,
  searchValue,
  onSearch,
  showBuyerColumn,
}: PromiseToPaysTableProps) {
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);

  const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: string[] } | null>(null);
  const [promisedDateRange, setPromisedDateRange] = useState<{ start: string; end: string }>({
    start: '',
    end: '',
  });
  const [amountRange, setAmountRange] = useState<{ min: string; max: string }>({
    min: '',
    max: '',
  });

  const allTableData: PromiseToPayTableRow[] = useMemo(
    () =>
      promiseToPays.map((p) => ({
        ...p,
        buyerNameFilter: p.buyer?.name || '-',
        createdByName: p.createdByUser
          ? `${p.createdByUser.firstName || ''} ${p.createdByUser.lastName || ''}`.trim() ||
            p.createdByUser.email ||
            '-'
          : '-',
      })),
    [promiseToPays]
  );

  const tableData = useMemo(() => {
    let rows = allTableData;

    if (selectedFilters) {
      rows = rows.filter((row) => {
        const statusMatch =
          !selectedFilters.status?.length || selectedFilters.status.includes(row.status || '');
        const typeMatch =
          !selectedFilters.type?.length || selectedFilters.type.includes(row.type || '');
        const sourceMatch =
          !selectedFilters.source?.length || selectedFilters.source.includes(row.source || '');
        const customerMatch =
          !selectedFilters.buyerNameFilter?.length ||
          selectedFilters.buyerNameFilter.includes(row.buyerNameFilter);
        const createdByMatch =
          !selectedFilters.createdByName?.length ||
          selectedFilters.createdByName.includes(row.createdByName);
        const branchMatch =
          !selectedFilters.branch?.length ||
          selectedFilters.branch.includes(row.buyer?.branch ?? '');
        return (
          statusMatch && typeMatch && sourceMatch && customerMatch && createdByMatch && branchMatch
        );
      });
    }

    if (promisedDateRange.start) {
      rows = rows.filter(
        (row) =>
          row.promisedPaymentDate &&
          new Date(row.promisedPaymentDate) >= new Date(promisedDateRange.start)
      );
    }
    if (promisedDateRange.end) {
      rows = rows.filter(
        (row) =>
          row.promisedPaymentDate &&
          new Date(row.promisedPaymentDate) <= new Date(`${promisedDateRange.end}T23:59:59`)
      );
    }

    if (amountRange.min !== '') {
      rows = rows.filter((row) => (row.amount ?? 0) >= Number(amountRange.min));
    }
    if (amountRange.max !== '') {
      rows = rows.filter((row) => (row.amount ?? 0) <= Number(amountRange.max));
    }

    return rows;
  }, [allTableData, selectedFilters, promisedDateRange, amountRange]);

  const statusOptions = useMemo(
    () => Array.from(new Set(allTableData.map((row) => row.status || ''))).filter(Boolean),
    [allTableData]
  );

  const typeOptions = useMemo(
    () => Array.from(new Set(allTableData.map((row) => row.type || ''))).filter(Boolean),
    [allTableData]
  );

  const sourceOptions = useMemo(
    () => Array.from(new Set(allTableData.map((row) => row.source || ''))).filter(Boolean),
    [allTableData]
  );

  const customerOptions = useMemo(
    () => Array.from(new Set(allTableData.map((row) => row.buyerNameFilter))).sort(),
    [allTableData]
  );

  const createdByOptions = useMemo(
    () => Array.from(new Set(allTableData.map((row) => row.createdByName))).sort(),
    [allTableData]
  );

  // Branch lives on the customer, not the promise - anything the user can't see is already
  // absent from `promiseToPays` (the backend scopes it), so this list is inherently branch-limited.
  const filterableBranches = useMemo(
    () =>
      collectBranchesFromBuyers(
        allTableData
          .map((row) => row.buyer)
          .filter((buyer) =>
            canUserAccessBuyerBranch({
              user,
              supplierId: currentSupplier?.id,
              buyerBranch: buyer?.branch,
            })
          )
      ),
    [allTableData, user, currentSupplier?.id]
  );

  const promisedDateRangeActiveLabel = useMemo(() => {
    if (!promisedDateRange.start && !promisedDateRange.end) return undefined;
    const fmt = (d: string) => (d ? moment(d).format('MM/DD/YYYY') : '...');
    return `Promised: ${fmt(promisedDateRange.start)} – ${fmt(promisedDateRange.end)}`;
  }, [promisedDateRange]);

  const amountRangeActiveLabel = useMemo(() => {
    if (amountRange.min === '' && amountRange.max === '') return undefined;
    const fmt = (v: string) => (v !== '' ? formatMoney(Number(v), 0) : '...');
    return `Amount: ${fmt(amountRange.min)} – ${fmt(amountRange.max)}`;
  }, [amountRange]);

  const filterItems = useMemo(
    () => [
      ...(showBuyerColumn && filterableBranches.length > 1
        ? [
            {
              id: 'branch',
              label: 'Filter by Branch',
              value: filterableBranches.map((branch) =>
                createCheckboxItem(
                  branch,
                  branch,
                  selectedFilters?.branch?.includes(branch) ?? false
                )
              ),
            },
          ]
        : []),
      {
        id: 'status',
        label: 'Filter by Status',
        value: statusOptions.map((status) =>
          createCheckboxItem(status, status, selectedFilters?.status?.includes(status) ?? false)
        ),
      },
      {
        id: 'type',
        label: 'Filter by Type',
        value: typeOptions.map((value) =>
          createCheckboxItem(
            typeLabels[value] || value,
            value,
            selectedFilters?.type?.includes(value) ?? false
          )
        ),
      },
      {
        id: 'source',
        label: 'Filter by Source',
        value: sourceOptions.map((value) =>
          createCheckboxItem(
            sourceLabels[value] || value,
            value,
            selectedFilters?.source?.includes(value) ?? false
          )
        ),
      },
      ...(showBuyerColumn
        ? [
            {
              id: 'buyerNameFilter',
              label: 'Filter by Customer',
              value: customerOptions.map((customer) =>
                createCheckboxItem(
                  customer,
                  customer,
                  selectedFilters?.buyerNameFilter?.includes(customer) ?? false
                )
              ),
            },
          ]
        : []),
      {
        id: 'createdByName',
        label: 'Filter by Created By',
        value: createdByOptions.map((name) =>
          createCheckboxItem(name, name, selectedFilters?.createdByName?.includes(name) ?? false)
        ),
      },
      {
        id: 'promisedPaymentDate',
        label: 'Filter by Promised Date',
        value: [],
        activeLabel: promisedDateRangeActiveLabel,
        customContent: (
          <div className="flex flex-col gap-2 pt-1">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 w-8">From</span>
              <input
                type="date"
                value={promisedDateRange.start}
                onChange={(e) =>
                  setPromisedDateRange((prev) => ({ ...prev, start: e.target.value }))
                }
                className="flex-1 text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 w-8">To</span>
              <input
                type="date"
                value={promisedDateRange.end}
                min={promisedDateRange.start || undefined}
                onChange={(e) => setPromisedDateRange((prev) => ({ ...prev, end: e.target.value }))}
                className="flex-1 text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
            {(promisedDateRange.start || promisedDateRange.end) && (
              <button
                onClick={() => setPromisedDateRange({ start: '', end: '' })}
                className="text-xs text-gray-400 hover:text-gray-600 text-left mt-0.5">
                Clear dates
              </button>
            )}
          </div>
        ),
      },
      {
        id: 'amount',
        label: 'Filter by Amount',
        value: [],
        activeLabel: amountRangeActiveLabel,
        customContent: (
          <div className="flex flex-col gap-2 pt-1">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 w-8">Min</span>
              <input
                type="number"
                value={amountRange.min}
                onChange={(e) => setAmountRange((prev) => ({ ...prev, min: e.target.value }))}
                className="flex-1 text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 w-8">Max</span>
              <input
                type="number"
                value={amountRange.max}
                onChange={(e) => setAmountRange((prev) => ({ ...prev, max: e.target.value }))}
                className="flex-1 text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
            {(amountRange.min !== '' || amountRange.max !== '') && (
              <button
                onClick={() => setAmountRange({ min: '', max: '' })}
                className="text-xs text-gray-400 hover:text-gray-600 text-left mt-0.5">
                Clear amounts
              </button>
            )}
          </div>
        ),
      },
    ],
    [
      selectedFilters,
      statusOptions,
      typeOptions,
      sourceOptions,
      customerOptions,
      createdByOptions,
      showBuyerColumn,
      filterableBranches,
      promisedDateRange,
      promisedDateRangeActiveLabel,
      amountRange,
      amountRangeActiveLabel,
    ]
  );

  const handleFilterChange = (data: Record<string, { value: string }[]>) => {
    const transformed = Object.fromEntries(
      Object.entries(data).map(([key, arr]) => [key, arr.map((item) => item.value)])
    );
    setSelectedFilters(transformed);
  };

  const columns: DillColumn<PromiseToPayTableRow>[] = useMemo(
    () => [
      ...(showBuyerColumn
        ? [
            {
              key: 'buyerName',
              label: 'Customer',
              sortable: true,
              width: 180,
              filterable: true,
              filterKey: 'buyerNameFilter',
              getSortValue: (row: PromiseToPayTableRow) => row.buyer?.name || '',
            },
          ]
        : []),
      {
        key: 'source',
        label: 'Source',
        sortable: true,
        width: 120,
        filterable: true,
        getFilterLabel: (value: string) => sourceLabels[value] || value,
        getSortValue: (row) => row.source?.toString() || '',
      },
      {
        key: 'type',
        label: 'Type',
        sortable: true,
        width: 150,
        filterable: true,
        getFilterLabel: (value: string) => typeLabels[value] || value,
        getSortValue: (row) => row.type?.toString() || '',
      },
      {
        key: 'promisedPaymentDate',
        label: 'Promised Date',
        sortable: true,
        width: 150,
        filterable: true,
        filterType: 'dateRange',
        getSortValue: (row) =>
          row.promisedPaymentDate ? new Date(row.promisedPaymentDate).getTime() : 0,
      },
      {
        key: 'amount',
        label: 'Amount',
        sortable: true,
        numeric: true,
        width: 120,
        filterable: true,
        filterType: 'range',
        getSortValue: (row) => row.amount || 0,
      },
      {
        key: 'status',
        label: 'Status',
        sortable: true,
        width: 120,
        filterable: true,
        getSortValue: (row) => row.status || '',
      },
      {
        key: 'linkedInvoiceIds',
        label: 'Invoices',
        sortable: false,
        width: 100,
        getSearchValue: (row) => String(row.references?.length || 0),
      },
      {
        key: 'createdByUser',
        label: 'Created By',
        sortable: false,
        width: 200,
      },
      {
        key: 'createdAt',
        label: 'Created',
        sortable: true,
        width: 130,
        filterable: true,
        filterType: 'dateRange',
        getSortValue: (row) => (row.createdAt ? new Date(row.createdAt).getTime() : 0),
      },
      {
        key: 'actions',
        label: '',
        sortable: false,
        stickyRight: true,
        width: 180,
      },
    ],
    []
  );

  const columnRenderers: DillColumn<PromiseToPayWithUser>[] = useMemo(
    () => [
      {
        key: 'buyerName',
        label: 'Customer',
        render: (_, row) => (
          <p className="text-xs text-left w-full truncate">{row.buyer?.name || '-'}</p>
        ),
      },
      {
        key: 'source',
        label: 'Source',
        render: (value) => (
          <p className="text-xs text-left w-full truncate">{sourceLabels[value] || value || '-'}</p>
        ),
      },
      {
        key: 'type',
        label: 'Type',
        render: (value) => (
          <p className="text-xs text-left w-full truncate">{typeLabels[value] || value || '-'}</p>
        ),
      },
      {
        key: 'promisedPaymentDate',
        label: 'Promised Date',
        render: (value) => (
          <p className="text-xs text-left w-full">
            {value ? moment(value).format('MM/DD/YYYY') : '-'}
          </p>
        ),
      },
      {
        key: 'amount',
        label: 'Amount',
        numeric: true,
        render: (value) => (
          <p className="text-xs text-right w-full">{value != null ? formatMoney(value, 2) : '-'}</p>
        ),
      },
      {
        key: 'status',
        label: 'Status',
        render: (value) => (
          <div className="w-full">
            <span
              className={twMerge(
                'px-2 py-1 rounded-full text-xs font-medium',
                statusColors[value as string] || 'bg-gray-100 text-gray-800'
              )}>
              {value as string}
            </span>
          </div>
        ),
      },
      {
        key: 'linkedInvoiceIds',
        label: 'Invoices',
        render: (_, row) => (
          <p className="text-xs text-left w-full">{row.references?.length || 0}</p>
        ),
      },
      {
        key: 'createdByUser',
        label: 'Created By',
        render: (_, row) => (
          <p className="text-xs text-left w-full truncate">
            {row.createdByUser
              ? `${row.createdByUser.firstName || ''} ${row.createdByUser.lastName || ''}`.trim() ||
                row.createdByUser.email ||
                '-'
              : '-'}
          </p>
        ),
      },
      {
        key: 'createdAt',
        label: 'Created',
        render: (value) => (
          <p className="text-xs text-left w-full">
            {value ? moment(value as string).format('MM/DD/YYYY') : '-'}
          </p>
        ),
      },
      {
        key: 'actions',
        label: '',
        render: (_, row) => (
          <div className="flex items-center gap-2 min-w-max">
            {row.status === 'ACTIVE' && (
              <div onClick={(e) => e.stopPropagation()}>
                <DillButton
                  variant="icon-default"
                  icon={<Edit2 size={14} />}
                  onClick={() => onEdit(row)}
                  height={34}
                  width={34}
                />
              </div>
            )}
            {onCancel && (
              <div
                className={row.status !== 'ACTIVE' ? 'invisible' : ''}
                onClick={(e) => e.stopPropagation()}>
                <DillButton variant="secondary" label="Cancel" onClick={() => onCancel(row.id)} />
              </div>
            )}

            {row.status !== 'BROKEN' && row.status !== 'CANCELLED' && (
              <div onClick={(e) => e.stopPropagation()}>
                <DillButton
                  variant="icon-red"
                  icon={<Trash2 size={14} />}
                  onClick={() => onDelete(row.id)}
                  height={34}
                  width={34}
                />
              </div>
            )}
          </div>
        ),
      },
    ],
    [onCancel, onDelete, onEdit]
  );

  const finalColumns: DillColumn<PromiseToPayTableRow>[] = columns.map((col) => {
    const renderer = columnRenderers.find((r) => r.key === col.key);
    return renderer ? { ...col, render: renderer.render } : col;
  });

  const defaultSort = sortOptions.sortBy
    ? { key: sortOptions.sortBy, dir: sortOptions.sortOrder }
    : undefined;

  return (
    <DillTable<PromiseToPayTableRow>
      columns={finalColumns}
      data={tableData}
      rowKey={(row) => row.id}
      sortable
      sortCycle="3state"
      defaultSort={defaultSort}
      onRowClick={onRowClick}
      striped={false}
      virtualize
      rowHeight={60}
      emptyMessage="No Promises to Pay"
      showCount={false}
      showActiveFilters
      toolbarLeft={
        <div className="w-[260px]">
          <DillDropDownFilter
            placeholder="Filters"
            closeMenu={false}
            isMulti
            showSelectedItems
            multiValue={selectedFilters}
            items={filterItems}
            showMinimalDesign
            onMultiSelectChange={handleFilterChange}
            inputHeight={30}
            numberOfItemsToShow={2}
          />
        </div>
      }
      toolbarRight={toolbarRight}
      searchValue={searchValue}
      onSearch={onSearch}
      scrollX
    />
  );
}

export default PromiseToPaysTable;
